import type { Reservation, ReservationItem, SailRef, Vehicle } from '@/root/shared-types/reservation/reservation-types'
import { isPromotionItem } from '@/root/shared-helpers/reservation/reservation-item-filters'
import { recordedWeightOnSail } from './vehicle-weight'

export interface EditCartRow {
  priceCategory: string
  title: string
  pricePerUnit: number
  quantity: number
  total: number
  /** Quantity or total differs from the committed reservation; always false without one. */
  isChanged: boolean
  /** Licence plates of the non-bicycle vehicles the row's items own, without duplicates. */
  plates: string[]
  /** Weight corrected by hand for this leg's sail, when there is one. */
  correctedWeightInKg?: number
}

export interface EditCartLeg {
  seqN: number
  routeTitle: string
  sailRef?: SailRef
  rows: EditCartRow[]
  residentIds: string[]
}

export interface EditCartModel {
  /** In `sailPackages` order (by departure date), not by `seqN`: a reversed seqN must not swap the directions. */
  legs: EditCartLeg[]
  driverDiscount: { title: string; amount: number } | null
  totals: { totalPrice: number; paidAmount: number; toPayAmount: number; toRefundAmount: number }
  isEmpty: boolean
}

interface LegVisibility {
  hideFirstLeg?: boolean
  hideSecondLeg?: boolean
}

const rowKey = (sailPackageSeqN: number, priceCategory: string) => `${sailPackageSeqN}-${priceCategory}`

const getItemVehicles = (item: ReservationItem, vehicles: Vehicle[] | undefined): Vehicle[] => {
  if (item.type !== 'VEHICLE') return []
  return (vehicles ?? []).filter(
    (vehicle) => vehicle.type !== 'BICYCLE' && item.ownerSeqNs?.includes(vehicle.seqN) && Boolean(vehicle.licencePlate)
  )
}

const getCommittedTotals = (committed: Reservation | undefined) => {
  const totals = new Map<string, { price: number; quantity: number }>()
  for (const item of committed?.items ?? []) {
    if (isPromotionItem(item)) continue
    const key = rowKey(item.sailPackageSeqN ?? 1, item.priceCategory)
    const sum = totals.get(key) ?? { price: 0, quantity: 0 }
    totals.set(key, { price: sum.price + item.price, quantity: sum.quantity + item.quantity })
  }
  return totals
}

export const getEditCartModel = (
  current: Reservation | undefined,
  committed: Reservation | undefined,
  { hideFirstLeg, hideSecondLeg }: LegVisibility
): EditCartModel => {
  const items = current?.items ?? []
  const committedTotals = getCommittedTotals(committed)

  const itemsBySeqN = new Map<number, ReservationItem[]>()
  for (const item of items) {
    const seqN = item.sailPackageSeqN ?? 1
    itemsBySeqN.set(seqN, [...(itemsBySeqN.get(seqN) ?? []), item])
  }

  const promotions: ReservationItem[] = []
  const legs: EditCartLeg[] = []

  ;(current?.sailPackages ?? []).forEach((sailPackage, index) => {
    const seqN = sailPackage.seqN ?? 1
    const legItems = itemsBySeqN.get(seqN)
    if (legItems === undefined || (index === 0 && hideFirstLeg) || (index === 1 && hideSecondLeg)) return

    const sailRef = sailPackage.sailRefs?.[0]
    const rows = new Map<string, EditCartRow>()

    for (const item of legItems) {
      if (isPromotionItem(item)) {
        promotions.push(item)
        continue
      }
      const row = rows.get(item.priceCategory) ?? {
        priceCategory: item.priceCategory,
        title: item.priceCategoryTranslation,
        pricePerUnit: item.pricePerUnit,
        quantity: 0,
        total: 0,
        isChanged: false,
        plates: [],
      }
      row.quantity += item.quantity
      row.total += item.price
      for (const vehicle of getItemVehicles(item, current?.vehicles)) {
        if (!row.plates.includes(vehicle.licencePlate)) row.plates.push(vehicle.licencePlate)
        const correctedWeightInKg = sailRef === undefined ? null : recordedWeightOnSail(vehicle, sailRef.sailRefId)
        if (correctedWeightInKg !== null) row.correctedWeightInKg = correctedWeightInKg
      }
      rows.set(item.priceCategory, row)
    }

    for (const row of rows.values()) {
      const committedRow = committedTotals.get(rowKey(seqN, row.priceCategory))
      row.isChanged =
        committed !== undefined &&
        (row.quantity !== (committedRow?.quantity ?? 0) || row.total !== (committedRow?.price ?? 0))
    }

    const residentOwnerSeqNs = new Set(
      legItems.filter((item) => item.resident).flatMap((item) => item.ownerSeqNs ?? [])
    )
    const residentIds = (current?.guests ?? []).flatMap((guest) =>
      residentOwnerSeqNs.has(guest.seqN) && guest.personalIdentificationNumber
        ? [guest.personalIdentificationNumber]
        : []
    )

    legs.push({
      seqN,
      routeTitle: sailRef?.routeLegTitle ?? sailPackage.title ?? '',
      sailRef,
      rows: [...rows.values()],
      residentIds,
    })
  })

  const [firstPromotion] = promotions
  const paymentInfo = current?.paymentInfo

  return {
    legs,
    driverDiscount:
      firstPromotion === undefined
        ? null
        : {
            title: firstPromotion.priceCategoryTranslation,
            amount: promotions.reduce((sum, promotion) => sum + promotion.price, 0),
          },
    totals: {
      totalPrice: paymentInfo?.totalPrice?.amount ?? 0,
      paidAmount: paymentInfo?.paidAmount ?? 0,
      toPayAmount: paymentInfo?.toPayAmount ?? 0,
      toRefundAmount: paymentInfo?.toRefundAmount ?? 0,
    },
    isEmpty: items.length === 0,
  }
}
